/**
 * File kinds and sizes for attachments, uploads and file lists: which icon and
 * tint a file gets, and „1,5 MB“ in the reader's locale.
 */
import type { NxHue } from '../lib/hue';

export type NxFileKind =
  | 'image'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'presentation'
  | 'archive'
  | 'audio'
  | 'video'
  | 'code'
  | 'text'
  | 'email'
  | 'folder'
  | 'other';

const BY_EXTENSION: Record<string, NxFileKind> = {
  pdf: 'pdf',
  doc: 'document',
  docx: 'document',
  odt: 'document',
  rtf: 'document',
  pages: 'document',
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  calcz: 'spreadsheet',
  ods: 'spreadsheet',
  csv: 'spreadsheet',
  numbers: 'spreadsheet',
  ppt: 'presentation',
  pptx: 'presentation',
  odp: 'presentation',
  key: 'presentation',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  tgz: 'archive',
  mp3: 'audio',
  wav: 'audio',
  ogg: 'audio',
  m4a: 'audio',
  flac: 'audio',
  mp4: 'video',
  mov: 'video',
  webm: 'video',
  mkv: 'video',
  avi: 'video',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  svg: 'image',
  heic: 'image',
  bmp: 'image',
  tif: 'image',
  tiff: 'image',
  txt: 'text',
  md: 'text',
  log: 'text',
  eml: 'email',
  msg: 'email',
  json: 'code',
  xml: 'code',
  yml: 'code',
  yaml: 'code',
  js: 'code',
  ts: 'code',
  html: 'code',
  css: 'code',
  sql: 'code',
  go: 'code',
  py: 'code',
  sh: 'code',
};

/** Lower-case extension of a file name without the dot, `''` when it has none. */
export function fileExtension(name: string | null | undefined): string {
  const base =
    String(name ?? '')
      .split(/[\\/]/)
      .pop() ?? '';
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

/** Characters of the stem that stay visible next to the extension. */
const TAIL_KEEP = 8;
/** Shorter stems stay whole in the head. */
const MIN_SPLIT_STEM = 6;

const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

/** What the reader sees as characters: „ü“ stays one even when decomposed (macOS). */
function graphemes(text: string): string[] {
  return Array.from(graphemeSegmenter.segment(text), (part) => part.segment);
}

/**
 * A file name split for truncation in the middle: `head` may shrink to an
 * ellipsis, `tail` — the last few characters of the stem plus the extension —
 * stays visible, so „Angebot_Kunde_Mül…_2026-10.pdf“ still shows what it is.
 * `head + tail` is the name unchanged; `ext` is the extension with its
 * dot (also the end of `tail`), empty when there is none. Only a short,
 * space-free extension counts as one; a long stem keeps 8 characters, a short
 * one half. A stem under 6 characters is not split (`tail` and `ext` empty): a
 * head of one or two letters would leave a gap next to the head's minimum width.
 * Such a name has no tail, so in a very narrow chip its extension is cut with
 * the rest.
 */
export function splitFileName(name: string): { head: string; tail: string; ext: string } {
  const chars = graphemes(name);
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 && /^\.[^\s.]{1,10}$/.test(name.slice(dot)) ? name.slice(dot) : '';
  const stemLength = chars.length - graphemes(ext).length;
  if (stemLength < MIN_SPLIT_STEM) return { head: name, tail: '', ext: '' };
  const keep = Math.min(TAIL_KEEP, Math.floor(stemLength / 2));
  const cut = stemLength - keep;
  return { head: chars.slice(0, cut).join(''), tail: chars.slice(cut).join(''), ext };
}

/**
 * Kind of a file from its MIME type, else its extension. The MIME type wins
 * where it is specific; `application/octet-stream` and friends fall back to
 * the name.
 */
export function fileKind(name?: string | null, type?: string | null): NxFileKind {
  const mime = String(type ?? '').toLowerCase();
  if (mime === 'inode/directory') return 'folder';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf') return 'pdf';
  if (mime === 'message/rfc822') return 'email';
  if (mime.includes('spreadsheet') || mime.includes('ms-excel') || mime === 'text/csv') {
    return 'spreadsheet';
  }
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'presentation';
  if (mime.includes('wordprocessing') || mime.includes('msword')) return 'document';
  if (mime.includes('zip') || mime.includes('compressed') || mime.includes('tar')) return 'archive';
  const byName = BY_EXTENSION[fileExtension(name)];
  if (byName) return byName;
  if (mime.startsWith('text/')) return 'text';
  return 'other';
}

/** Tint of each kind: a category hue, so a file list reads at a glance. */
export const FILE_KIND_HUE: Record<NxFileKind, NxHue> = {
  image: 'violet',
  pdf: 'rose',
  document: 'sky',
  spreadsheet: 'lime',
  presentation: 'amber',
  archive: 'slate',
  audio: 'teal',
  video: 'violet',
  code: 'slate',
  text: 'slate',
  email: 'sky',
  folder: 'amber',
  other: 'slate',
};

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/**
 * Human-readable size in the reader's locale: `512 B`, `1,5 KB`, `10 MB`
 * (base 1024; one decimal below 10, none above). Negative or non-finite input
 * reads as `0 B`.
 */
export function formatBytes(bytes: number | null | undefined, locale?: string): string {
  let value = typeof bytes === 'number' && Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  const digits = unit > 0 && value < 10 ? 1 : 0;
  const text = new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value);
  return `${text} ${UNITS[unit]}`;
}
